import React from 'react';
import { useHits } from 'react-instantsearch';
import { SequenceCard } from '@/components/sequences/SequenceCard';
import { Sequence } from '@/types/sequence';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { SequenceTransformService, TypesenseHit } from '@/lib/search/SequenceTransformService';

interface HitsProps {
  onClearFilters?: () => void;
  emptyComponent?: React.ReactNode;
}

export const Hits: React.FC<HitsProps> = ({ onClearFilters, emptyComponent }) => {
  const { hits } = useHits();

  // Transform Typesense hits to Sequence objects using centralized service
  // The service automatically filters out invalid hits
  let sequences: Sequence[] = [];

  try {
    sequences = SequenceTransformService.transformHitsToSequences(hits as TypesenseHit[]);
  } catch (error) {
    console.error('Error transforming search hits:', error);
    console.error('Problematic hits:', hits);
    // Return empty array on error - component will show "no results" message
    sequences = [];
  }

  if (sequences.length === 0) {
    // Use custom empty component if provided
    if (emptyComponent) {
      return <>{emptyComponent}</>;
    }

    // Default empty state
    return (
      <Card className="text-center py-12">
        <CardContent>
          <h3 className="text-lg font-semibold mb-2">No sequences found</h3>
          <p className="text-muted-foreground mb-4">
            Try adjusting your search terms or filters
          </p>
          {onClearFilters && (
            <Button onClick={onClearFilters}>Clear all filters</Button>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {sequences.map((sequence) => (
        <SequenceCard key={sequence.id} sequence={sequence} />
      ))}
    </div>
  );
};